import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Tab, Tabs, Tooltip } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import { getFileSize } from '../../../../utils';
import { getUploadSessionFilesPageApi, getErrorMessage, normalizeUploadPath } from '../apiServices';
import { LIVE_STATUS, isRecentActivity } from '../liveUploads';
import { sessionStatusConfig, uploadStatusConfig } from '../constants';
import { folderLink, formatDuration, formatTime, getConfig, StatusChip } from './sessionDisplay';

// Rows fetched or shown per step; failed lists can run to thousands
const PAGE_SIZE = 100;
// Another tab's or device's progress only moves with its 30 s heartbeat, so polling faster gains nothing
const IN_PROGRESS_REFRESH_MS = 10 * 1000;
// Per-file speed for a run in this tab: sampled at most this often and smoothed
const FILE_SPEED_SAMPLE_MS = 1000;
// Reason chips shown above the failed list (the most common ones)
const MAX_REASON_CHIPS = 6;

const TABS = { PROGRESS: 'progress', FAILED: 'failed' };

const splitPath = (path) => {
  const value = path || '';
  const slash = value.lastIndexOf('/');
  return { dir: value.slice(0, slash + 1), name: value.slice(slash + 1) };
};

const CopyPathButton = ({ path }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  if (!navigator.clipboard) return null;
  return (
    <Tooltip arrow title={copied ? 'Copied' : 'Copy path'}>
      <IconButton size="small" className="session-files__copy" onClick={() => navigator.clipboard.writeText(path).then(() => setCopied(true)).catch(() => {})}>
        <FileCopyOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};

const FileRow = ({ path, size, icon, children }) => {
  const { dir, name } = splitPath(path);
  return (
    <div className="session-files__row">
      <span className="session-files__icon">{icon}</span>
      <div className="session-files__main">
        <div className="session-files__name" title={path}>{name}</div>
        {dir ? <div className="session-files__dir" title={dir}>{dir}</div> : null}
        {children}
      </div>
      <CopyPathButton path={path} />
      <div className="session-files__size">{getFileSize(size)}</div>
    </div>
  );
};

const FileProgress = ({ bytes, size, note, color }) => {
  const percentage = size ? Math.min(100, (bytes / size) * 100) : 0;
  return (
    <div className="session-files__progress">
      <LinearProgress
        variant="determinate"
        value={percentage}
        className="session-files__bar"
        classes={{ bar: 'session-files__bar-fill' }}
        style={{ '--bar-color': color || uploadStatusConfig.UPLOADING.bar }}
      />
      <span><b>{Math.floor(percentage)}%</b> · {getFileSize(bytes)} of {getFileSize(size)}{note ? ` · ${note}` : ''}</span>
    </div>
  );
};

const RowsSkeleton = () => [0, 1, 2, 3].map(index => (
  <div key={index} className="session-files__row">
    <Skeleton variant="circle" width={18} height={18} />
    <div className="session-files__main">
      <Skeleton width="45%" />
      <Skeleton width="70%" height={12} />
    </div>
  </div>
));

const Stat = ({ label, value, color }) => (
  <div className="session-files__stat">
    <strong style={{ color }}>{value === null || value === undefined ? '--' : value.toLocaleString()}</strong>
    <span>{label}</span>
  </div>
);

const emptyPage = { files: [], count: null, nextCursor: null, loading: false, error: null, loaded: false };

/**
 * Files of one upload session: what is uploading right now and what failed (with the reason).
 * For a run in this tab, "In progress" comes from the engine (exact, live, with per-file speed) and its
 * failures include reasons the server may not have recorded. Otherwise everything comes from the server,
 * where an uploading file's progress moves with its uploader's 30 s heartbeat.
 */
const SessionFilesDialog = ({ session, live, onClose }) => {
  const [tab, setTab] = useState(TABS.PROGRESS);
  const [serverProgress, setServerProgress] = useState(emptyPage);
  const [serverFailed, setServerFailed] = useState(emptyPage);
  const [localFailedShown, setLocalFailedShown] = useState(PAGE_SIZE);
  const [reasonFilter, setReasonFilter] = useState(null);
  const [fileSpeeds, setFileSpeeds] = useState({});
  // Per status: the next page's cursor and the latest request, so a slow older reply can't overwrite a newer one
  const cursors = useRef({});
  const requests = useRef({});
  // Statuses where "Load more" was used; refreshing those would collapse the list back to the first page
  const expanded = useRef({});
  // fileId -> { at, bytes } of the last speed sample
  const speedSamples = useRef({});

  const api = { assetType: session.asset_type, assetSlug: session.asset_slug, uploadId: session.upload_id };
  const runningHere = !!live && (live.status === LIVE_STATUS.UPLOADING || live.status === LIVE_STATUS.PAUSED);
  const pausedHere = runningHere && live.status === LIVE_STATUS.PAUSED;
  const localFailures = (live && live.failures) || [];
  const activeFiles = (runningHere && live.activeFiles) || [];

  // Loads the first page of a status (replacing the list) or its next page (appending)
  const loadPage = useCallback((status, setPage, append) => {
    expanded.current[status] = append;
    const request = (requests.current[status] || 0) + 1;
    requests.current[status] = request;
    setPage(page => ({ ...page, loading: true, error: null }));
    getUploadSessionFilesPageApi({ ...api, status, cursor: append ? cursors.current[status] : null, limit: PAGE_SIZE })
      .then(result => {
        if (requests.current[status] !== request) return;
        cursors.current[status] = result.nextCursor;
        setPage(current => ({
          files: append ? [...current.files, ...result.files] : result.files,
          count: result.count,
          nextCursor: result.nextCursor,
          loading: false,
          error: null,
          loaded: true,
        }));
      })
      .catch(error => {
        if (requests.current[status] !== request) return;
        setPage(current => ({ ...current, loading: false, error: getErrorMessage(error, 'Could not load the files. Check your connection and try again.') }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.upload_id]);

  // In progress from the server (not needed for a run in this tab): first page, refreshed while open
  useEffect(() => {
    if (tab !== TABS.PROGRESS || runningHere || !session.asset_slug) return undefined;
    loadPage('UPLOADING', setServerProgress, false);
    const timer = setInterval(() => {
      if (!expanded.current.UPLOADING) loadPage('UPLOADING', setServerProgress, false);
    }, IN_PROGRESS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [tab, runningHere, session.asset_slug, loadPage]);

  // Failed from the server: loaded when the tab opens; failures don't change on their own
  useEffect(() => {
    if (tab === TABS.FAILED && session.asset_slug) loadPage('FAILED', setServerFailed, false);
  }, [tab, session.asset_slug, loadPage]);

  // Per-file speed for a run in this tab, from the byte deltas between the engine's snapshots
  useEffect(() => {
    if (!activeFiles.length) {
      speedSamples.current = {};
      return;
    }
    const now = Date.now();
    const samples = {};
    const speeds = {};
    activeFiles.forEach(file => {
      const previous = speedSamples.current[file.fileId];
      if (!previous) {
        samples[file.fileId] = { at: now, bytes: file.bytes, speed: 0 };
      } else if (now - previous.at < FILE_SPEED_SAMPLE_MS) {
        samples[file.fileId] = previous;
      } else {
        const instant = Math.max(0, file.bytes - previous.bytes) / ((now - previous.at) / 1000);
        samples[file.fileId] = { at: now, bytes: file.bytes, speed: previous.speed ? previous.speed * 0.6 + instant * 0.4 : instant };
      }
      speeds[file.fileId] = samples[file.fileId].speed;
    });
    speedSamples.current = samples;
    setFileSpeeds(speeds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live && live.activeFiles]);

  // This tab's failures come first (exact reasons, including ones the server couldn't be told about);
  // server failures already listed there aren't repeated. Memoized because the dialog re-renders with
  // every progress update; the engine appends to the same failures array, so its length is a dependency.
  const { allFailed, reasons } = useMemo(() => {
    const localKeys = new Set(localFailures.map(failure => normalizeUploadPath(failure.path)));
    const merged = [
      ...localFailures.map(failure => ({ key: `local-${failure.path}`, path: failure.path, size: failure.size, reason: failure.error, at: null })),
      ...serverFailed.files
        .filter(file => !localKeys.has(normalizeUploadPath(file.path)))
        .map(file => ({ key: file.fileId, path: file.path, size: file.size, reason: file.errorMessage || 'Upload failed', at: file.updatedAt })),
    ];
    // Most common reasons among the loaded failures, as filter chips
    const counts = new Map();
    merged.forEach(failure => counts.set(failure.reason, (counts.get(failure.reason) || 0) + 1));
    return { allFailed: merged, reasons: [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_REASON_CHIPS) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFailures, localFailures.length, serverFailed.files]);

  const filteredFailed = allFailed.filter(failure => !reasonFilter || failure.reason === reasonFilter);
  // Local failures are revealed 100 at a time; server ones arrive a page at a time
  const localShownCount = filteredFailed.filter(failure => failure.at === null).length;
  const visibleFailed = filteredFailed.filter((failure, index) => failure.at !== null || index < localFailedShown);

  const totalFiles = Number(session.total_files) || 0;
  const completedFiles = Number(session.completed_files) || 0;
  const failedCount = Math.max(Number(session.failed_files) || 0, localFailures.length, serverFailed.count || 0);
  const progressCount = runningHere ? activeFiles.length : serverProgress.count;
  const waitingCount = runningHere ? Math.max(0, totalFiles - completedFiles - failedCount - activeFiles.length) : null;
  const percentage = Math.min(100, session.progress_percentage || 0);
  const speed = runningHere && !pausedHere ? live.speed || 0 : 0;
  const remainingBytes = Math.max(0, (Number(session.total_bytes) || 0) - (Number(session.uploaded_bytes) || 0));
  const eta = speed > 0 && remainingBytes > 0 ? formatDuration(remainingBytes / speed) : null;
  const link = folderLink(session);
  const config = getConfig(session.status);

  const switchTab = (value) => {
    setTab(value);
    setReasonFilter(null);
  };

  const renderInProgress = () => {
    if (runningHere) {
      return (
        <>
          <div className="session-files__hint">
            {pausedHere ? 'Paused. These files continue when the upload is resumed.' : 'Files being sent from this tab right now.'}
          </div>
          {activeFiles.map(file => {
            const fileSpeed = pausedHere ? 0 : fileSpeeds[file.fileId] || 0;
            const fileEta = fileSpeed > 0 && file.size > file.bytes ? formatDuration((file.size - file.bytes) / fileSpeed) : null;
            const note = pausedHere ? 'Paused' : fileSpeed > 0 ? `${getFileSize(fileSpeed)}/s${fileEta ? ` · ${fileEta} left` : ''}` : 'Starting...';
            return (
              <FileRow
                key={file.fileId}
                path={file.path}
                size={file.size}
                icon={pausedHere ? <PauseCircleFilledIcon style={{ color: sessionStatusConfig.PAUSED.color }} /> : <CloudUploadIcon style={{ color: uploadStatusConfig.UPLOADING.color }} />}
              >
                <FileProgress bytes={file.bytes} size={file.size} note={note} color={pausedHere ? sessionStatusConfig.PAUSED.bar : undefined} />
              </FileRow>
            );
          })}
          {!activeFiles.length ? <div className="session-files__empty">{pausedHere ? 'Nothing is being sent while paused.' : 'Preparing the next files...'}</div> : null}
          {waitingCount ? <div className="session-files__footnote">{waitingCount.toLocaleString()} more file{waitingCount > 1 ? 's' : ''} waiting to start</div> : null}
        </>
      );
    }
    return (
      <>
        <div className="session-files__hint">
          Uploading in another tab, on another device or by another user. Progress updates about every 30 seconds.
        </div>
        {serverProgress.error ? <Alert severity="error" className="session-files__alert">{serverProgress.error}</Alert> : null}
        {serverProgress.loading && !serverProgress.loaded ? <RowsSkeleton /> : null}
        {serverProgress.files.map(file => {
          // No heartbeat for 2 minutes: whoever was sending it has probably stopped
          const stalled = file.updatedAt && !isRecentActivity(file.updatedAt);
          return (
            <FileRow
              key={file.fileId}
              path={file.path}
              size={file.size}
              icon={stalled ? <WarningIcon style={{ color: sessionStatusConfig.INTERRUPTED.color }} /> : <CloudUploadIcon style={{ color: uploadStatusConfig.UPLOADING.color }} />}
            >
              <FileProgress
                bytes={file.uploadedBytes}
                size={file.size}
                color={stalled ? sessionStatusConfig.INTERRUPTED.bar : undefined}
                note={file.updatedAt ? `${stalled ? 'no progress since' : 'updated'} ${moment(file.updatedAt).fromNow()}` : ''}
              />
            </FileRow>
          );
        })}
        {serverProgress.loaded && !serverProgress.files.length && !serverProgress.error ? <div className="session-files__empty">No files are uploading right now.</div> : null}
        {serverProgress.nextCursor !== null && serverProgress.loaded ?
          <Button size="small" color="primary" className="session-files__more" disabled={serverProgress.loading} onClick={() => loadPage('UPLOADING', setServerProgress, true)}>Load more</Button> : null}
      </>
    );
  };

  const renderFailed = () => (
    <>
      {reasons.length > 1 || reasonFilter ?
        <div className="session-files__reasons">
          {reasons.map(([reason, count]) => (
            <Chip
              key={reason}
              size="small"
              label={`${reason} (${count.toLocaleString()})`}
              title={reason}
              className="session-files__reason"
              color={reasonFilter === reason ? 'secondary' : 'default'}
              variant={reasonFilter === reason ? 'default' : 'outlined'}
              onClick={() => setReasonFilter(current => (current === reason ? null : reason))}
              onDelete={reasonFilter === reason ? () => setReasonFilter(null) : undefined}
            />
          ))}
        </div> : null
      }
      {serverFailed.error ? <Alert severity="error" className="session-files__alert">{serverFailed.error}</Alert> : null}
      {serverFailed.loading && !serverFailed.loaded && !localFailures.length ? <RowsSkeleton /> : null}
      {visibleFailed.map(failure => (
        <FileRow key={failure.key} path={failure.path} size={failure.size} icon={<ErrorIcon style={{ color: uploadStatusConfig.FAILED.color }} />}>
          <Tooltip arrow title={failure.at ? `Failed ${formatTime(failure.at)}` : 'Failed in this tab'}>
            <div className="session-files__error">{failure.reason}</div>
          </Tooltip>
        </FileRow>
      ))}
      {localShownCount > localFailedShown ?
        <Button size="small" color="primary" className="session-files__more" onClick={() => setLocalFailedShown(shown => shown + PAGE_SIZE)}>
          Show {Math.min(PAGE_SIZE, localShownCount - localFailedShown).toLocaleString()} more ({(localShownCount - localFailedShown).toLocaleString()} remaining)
        </Button> : null
      }
      {serverFailed.nextCursor !== null && serverFailed.loaded ?
        <Button size="small" color="primary" className="session-files__more" disabled={serverFailed.loading} onClick={() => loadPage('FAILED', setServerFailed, true)}>Load more</Button> : null}
      {serverFailed.loaded && !serverFailed.error && !allFailed.length ?
        <div className="session-files__empty">No failed files.</div> : null}
      {!!allFailed.length && !filteredFailed.length ? <div className="session-files__empty">No loaded failure has this reason.</div> : null}
    </>
  );

  return (
    <Dialog className="session-files" open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className="session-files__title">
        <div className="session-files__title-main">
          <div className="session-files__title-text">
            <span>{session.asset_name || 'Upload'}</span>
            <StatusChip status={session.status} />
          </div>
          <div className="session-files__title-sub">
            {session.created_by && session.created_by.name ? <span>Started by {session.created_by.name}</span> : null}
            {session.created_at ? <span>{formatTime(session.created_at)}</span> : null}
            {link ?
              <Link to={link} className="upload-progress__folder-link" onClick={onClose}>
                <FolderOpenIcon /> Open folder
              </Link> : null
            }
          </div>
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>

      <div className="session-files__summary">
        <div>
          <LinearProgress
            variant="determinate"
            value={percentage}
            className="session-files__bar session-files__bar--overall"
            classes={{ bar: 'session-files__bar-fill' }}
            style={{ '--bar-color': config.bar }}
          />
          <div className="session-files__overall-note">
            <b>{Math.floor(percentage)}%</b> · {getFileSize(session.uploaded_bytes || 0)} of {getFileSize(session.total_bytes || 0)}
            {speed > 0 ? ` · ${getFileSize(speed)}/s` : ''}
            {eta ? ` · about ${eta} left` : ''}
          </div>
        </div>
        <div className="session-files__stats">
          <Stat label="Completed" value={completedFiles} color={uploadStatusConfig.COMPLETED.color} />
          <Stat label="Uploading" value={progressCount} color={uploadStatusConfig.UPLOADING.color} />
          {waitingCount !== null ? <Stat label="Waiting" value={waitingCount} color={uploadStatusConfig.PENDING.color} /> : null}
          <Stat label="Failed" value={failedCount} color={uploadStatusConfig.FAILED.color} />
          <Stat label="Total" value={totalFiles} />
        </div>
      </div>

      <Tabs value={tab} onChange={(evt, value) => switchTab(value)} indicatorColor="primary" textColor="primary" className="session-files__tabs">
        <Tab value={TABS.PROGRESS} label="In progress" />
        <Tab value={TABS.FAILED} label="Failed" />
      </Tabs>

      <DialogContent className="session-files__content">
        {tab === TABS.PROGRESS ? renderInProgress() : renderFailed()}
      </DialogContent>
    </Dialog>
  );
};

export default SessionFilesDialog;
